// src/components/EditTruck.js

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  FaArrowLeft,
  FaBuilding,
  FaCalendarAlt,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaCube,
  FaDownload,
  FaExclamationTriangle,
  FaEye,
  FaEyeSlash,
  FaFileAlt,
  FaGasPump,
  FaHashtag,
  FaIdCard,
  FaIndustry,
  FaLayerGroup,
  FaLink,
  FaPlus,
  FaSave,
  FaShieldAlt,
  FaTrash,
  FaUpload,
} from "react-icons/fa";

import Select from "react-select";
import { useTranslation } from "react-i18next";

import axios from "../axiosConfig";
import { customSelectStyles } from "../styles/reactSelectStyles";
import "../styles/EditTruck.css";

import TruckFuelTab from "./TruckFuelTab";

import {
  isTruckDetailsCacheFresh,
  mergeTruckDetailsData,
  primeTruckDetailsCache,
  readTruckDetailsCache,
  writeTruckDetailsCache,
  patchTruckSummaryCaches,
} from "../utils/truckDetailsCache";

/* =========================================================
   CONSTANTS
========================================================= */

const EMPTY_TRUCK = {
  type_of_gas: "",
  brand: "",
  model: "",
  month: "",
  year: "",
  vin: "",
  plate: "",

  number_of_straps: "",
  number_of_ratchets: "",
  number_of_edge_protectors: "",
  number_of_anti_slip_mats: "",

  InspectionPhotoExpiry: "",
  TruckInsuranceExpiry: "",
  CommunityLicenceExpiry: "",

  status: "ACTIVE",
};

const EMPTY_FILES = {
  InspectionPhoto: null,
  VehicleRegistrationCertificate: null,
  CommunityLicence: null,
  TruckInsurance: null,
};

const GAS_VALUES = [
  "Diesel",
  "Compressed Natural Gas (CNG)",
  "Hybrid",
  "LNG",
  "Hydrogen",
  "Electric",
];

const FILES_BASE =
  (
    typeof process !==
      "undefined" &&
    (
      process.env
        .REACT_APP_FILES_BASE_URL ||
      process.env
        .REACT_APP_API_BASE_URL ||
      process.env
        .REACT_APP_API_URL
    )
  ) ||
  "https://api.loadevo.com";

/* =========================================================
   GENERIC HELPERS
========================================================= */

function formatDate(
  value
) {
  if (!value) {
    return "";
  }

  const raw =
    String(
      value
    ).trim();

  const isoMatch =
    raw.match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

  if (
    isoMatch
  ) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const portugueseMatch =
    raw.match(
      /^(\d{2})\/(\d{2})\/(\d{4})$/
    );

  if (
    portugueseMatch
  ) {
    return `${portugueseMatch[3]}-${portugueseMatch[2]}-${portugueseMatch[1]}`;
  }

  const parsedDate =
    new Date(
      raw
    );

  if (
    Number.isNaN(
      parsedDate.getTime()
    )
  ) {
    return "";
  }

  const year =
    parsedDate.getFullYear();

  const month =
    String(
      parsedDate.getMonth() +
        1
    ).padStart(
      2,
      "0"
    );

  const day =
    String(
      parsedDate.getDate()
    ).padStart(
      2,
      "0"
    );

  return `${year}-${month}-${day}`;
}

function formatDateForDisplay(
  value
) {
  const normalized =
    formatDate(
      value
    );

  if (
    !normalized
  ) {
    return "—";
  }

  const [
    year,
    month,
    day,
  ] =
    normalized.split(
      "-"
    );

  return `${day}/${month}/${year}`;
}

function extractFileName(
  path = ""
) {
  if (!path) {
    return "";
  }

  let clean =
    String(
      path
    )
      .replace(
        /\\/g,
        "/"
      )
      .split(
        "#"
      )[0]
      .split(
        "?"
      )[0];

  let name =
    clean
      .split(
        "/"
      )
      .pop() ||
    "";

  try {
    name =
      decodeURIComponent(
        name
      );
  } catch {
    // Mantemos o nome original.
  }

  return name;
}

function niceExistingFileName(
  value
) {
  if (!value) {
    return "";
  }

  if (
    typeof File !==
      "undefined" &&
    value instanceof File
  ) {
    return extractFileName(
      value.name ||
        ""
    );
  }

  if (
    typeof value ===
    "object"
  ) {
    return extractFileName(
      value.name ||
        value.url ||
        value.uri ||
        value.path ||
        ""
    );
  }

  return extractFileName(
    value
  );
}

function safeDownloadFileName(
  name,
  fallback =
    "document"
) {
  const clean =
    extractFileName(
      name ||
        fallback
    )
      .replace(
        /[/\\?%*:|"<>]/g,
        "-"
      )
      .trim();

  return (
    clean ||
    fallback
  );
}

function isPdfFile(
  value
) {
  if (!value) {
    return false;
  }

  if (
    typeof File !==
      "undefined" &&
    value instanceof File
  ) {
    return (
      value.type ===
        "application/pdf" ||
      /\.pdf$/i.test(
        value.name ||
          ""
      )
    );
  }

  const raw =
    typeof value ===
      "object"
      ? String(
          value.url ||
            value.uri ||
            value.path ||
            value.name ||
            ""
        )
      : String(
          value ||
            ""
        );

  const clean =
    raw
      .toLowerCase()
      .trim()
      .split(
        "#"
      )[0]
      .split(
        "?"
      )[0];

  return clean.endsWith(
    ".pdf"
  );
}

function isImageFile(
  value
) {
  if (!value) {
    return false;
  }

  if (
    typeof File !==
      "undefined" &&
    value instanceof File
  ) {
    return (
      String(
        value.type ||
          ""
      ).startsWith(
        "image/"
      ) ||
      /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(
        value.name ||
          ""
      )
    );
  }

  const raw =
    typeof value ===
      "object"
      ? String(
          value.url ||
            value.uri ||
            value.path ||
            value.name ||
            ""
        )
      : String(
          value ||
            ""
        );

  const clean =
    raw
      .toLowerCase()
      .trim()
      .split(
        "#"
      )[0]
      .split(
        "?"
      )[0];

  return /\.(jpg|jpeg|png|webp|gif|bmp|svg)$/i.test(
    clean
  );
}

function buildFileUrl(
  reference
) {
  if (
    !reference
  ) {
    return "";
  }

  if (
    typeof File !==
      "undefined" &&
    (
      reference instanceof File ||
      reference instanceof Blob
    )
  ) {
    try {
      return URL.createObjectURL(
        reference
      );
    } catch {
      return "";
    }
  }

  const raw =
    typeof reference ===
      "object"
      ? reference.url ||
        reference.uri ||
        reference.path ||
        reference.location ||
        ""
      : reference;

  const value =
    String(
      raw ||
        ""
    );

  if (
    !value
  ) {
    return "";
  }

  if (
    /^(https?:|blob:|data:)/i.test(
      value
    )
  ) {
    return value;
  }

  const clean =
    (
      "/" +
      value
    )
      .replace(
        /\\/g,
        "/"
      )
      .replace(
        /\/{2,}/g,
        "/"
      );

  return `${
    FILES_BASE.replace(
      /\/+$/,
      ""
    )
  }${clean}`;
}

function normalizeFileReference(
  value
) {
  if (!value) {
    return null;
  }

  if (
    typeof File !==
      "undefined" &&
    value instanceof File
  ) {
    return value;
  }

  if (
    typeof value ===
      "object"
  ) {
    const url =
      value.url ||
      value.uri ||
      value.path ||
      value.location ||
      "";

    if (
      !url
    ) {
      return null;
    }

    return {
      ...value,
      name:
        value.name ||
        extractFileName(
          url
        ),
      url,
    };
  }

  return {
    name:
      extractFileName(
        value
      ),
    url:
      value,
  };
}

function mapSourceToTruck(
  source = {}
) {
  return {
    ...EMPTY_TRUCK,

    vin:
      source.vin ??
      "",

    type_of_gas:
      source.type_of_gas ??
      source.typeOfGas ??
      "",

    brand:
      source.brand ??
      "",

    model:
      source.model ??
      "",

    plate:
      source.plate ??
      "",

    month:
      Number(
        source.Month ??
          source.month ??
          ""
      ) ||
      "",

    year:
      source.year ??
      "",

    number_of_straps:
      source.number_of_straps ??
      source.numberOfStraps ??
      "",

    number_of_ratchets:
      source.number_of_ratchets ??
      source.numberOfRatchets ??
      "",

    number_of_edge_protectors:
      source.number_of_edge_protectors ??
      source.numberOfEdgeProtectors ??
      "",

    number_of_anti_slip_mats:
      source.number_of_anti_slip_mats ??
      source.numberOfAntiSlipMats ??
      "",

    InspectionPhotoExpiry:
      formatDate(
        source.InspectionPhotoExpiry ??
          source.inspectionPhotoExpiry
      ),

    TruckInsuranceExpiry:
      formatDate(
        source.TruckInsuranceExpiry ??
          source.truckInsuranceExpiry
      ),

    CommunityLicenceExpiry:
      formatDate(
        source.CommunityLicenceExpiry ??
          source.communityLicenceExpiry
      ),

    status:
      source.status ??
      source.Status ??
      "ACTIVE",
  };
}

function mapSourceToFiles(
  source = {}
) {
  return {
    InspectionPhoto:
      normalizeFileReference(
        source.inspectionPhoto ??
          source.InspectionPhoto
      ),

    VehicleRegistrationCertificate:
      normalizeFileReference(
        source.vehicleRegistrationCertificate ??
          source.VehicleRegistrationCertificate
      ),

    CommunityLicence:
      normalizeFileReference(
        source.communityLicence ??
          source.CommunityLicence
      ),

    TruckInsurance:
      normalizeFileReference(
        source.truckInsurance ??
          source.TruckInsurance
      ),
  };
}

function isEmptyFieldValue(
  value
) {
  return (
    value === null ||
    value === undefined ||
    value === ""
  );
}

function fillOnlyMissingTruckFields(
  current,
  incoming
) {
  const next = {
    ...current,
  };

  Object.entries(
    incoming ||
      {}
  ).forEach(
    ([
      key,
      value,
    ]) => {
      if (
        isEmptyFieldValue(
          next[key]
        ) &&
        !isEmptyFieldValue(
          value
        )
      ) {
        next[key] =
          value;
      }
    }
  );

  return next;
}

function isNewBrowserFile(
  value
) {
  return (
    typeof File !==
      "undefined" &&
    value instanceof File
  );
}

function createEmptyTruckCard() {
  return {
    clientId:
      `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`,

    id: null,

    provider: "",

    cardNumber: "",

    cardLast4: "",

    expiryMonth: "",

    pin: "",

    hasPin: false,
  };
}

function mapSourceToCards(
  source
) {
  const list =
    Array.isArray(
      source
    )
      ? source
      : [];

  return list.map(
    (
      card
    ) => ({
      clientId:
        `saved-${
          card.id
        }`,

      id:
        card.id,

      provider:
        card.provider ??
        "",

      /*
       * Nunca recebemos o número completo
       * de um cartão existente.
       */
      cardNumber:
        "",

      cardLast4:
        card.cardLast4 ??
        "",

      expiryMonth:
        card.expiryMonth ??
        "",

      /*
       * Nunca recebemos o PIN atual.
       * Um valor escrito aqui é um replacement.
       */
      pin:
        "",

      hasPin:
        Boolean(
          card.hasPin
        ),
    })
  );
}

function cardErrorKey(
  clientId,
  field
) {
  return `card:${clientId}:${field}`;
}

function hasCardValue(
  card
) {
  return Boolean(
    card?.id
  ) ||
  [
    card?.provider,
    card?.cardNumber,
    card?.expiryMonth,
    card?.pin,
  ].some(
    (
      value
    ) => {
      return (
        String(
          value ??
            ""
        ).trim() !==
        ""
      );
    }
  );
}

/* =========================================================
   STATUS HELPERS
========================================================= */

const DAY_MS =
  24 *
  60 *
  60 *
  1000;

function daysUntilDate(
  value
) {
  const normalized =
    formatDate(
      value
    );

  if (
    !normalized
  ) {
    return null;
  }

  const target =
    new Date(
      `${normalized}T00:00:00`
    );

  if (
    Number.isNaN(
      target.getTime()
    )
  ) {
    return null;
  }

  const today =
    new Date();

  today.setHours(
    0,
    0,
    0,
    0
  );

  return Math.ceil(
    (
      target.getTime() -
      today.getTime()
    ) /
      DAY_MS
  );
}

function getDocumentStatus(
  file,
  hasExpiryField,
  t
) {
  if (!file) {
    return {
      tone: "missing",

      label: t(
        "documents.status.missing",
        {
          defaultValue: "Em falta",
        }
      ),
    };
  }

  if (!hasExpiryField) {
    return {
      tone: "expiring",

      label: t(
        "documents.status.expiring",
        {
          defaultValue: "Em breve",
        }
      ),
    };
  }

  return {
    tone: "loaded",

    label: t(
      "documents.status.loaded",
      {
        defaultValue: "Carregado",
      }
    ),
  };
}

function getTruckStatus(
  truck,
  t
) {
  const raw =
    String(
      truck?.status ||
        "ACTIVE"
    ).toUpperCase();

  const active =
    ![
      "INACTIVE",
      "ARCHIVED",
      "DELETED",
      "CANCELLED",
    ].includes(
      raw
    );

  return {
    active,

    label:
      active
        ? t(
            "status.active",
            {
              defaultValue:
                "Ativo",
            }
          )
        : t(
            "status.inactive",
            {
              defaultValue:
                "Inativo",
            }
          ),
  };
}

/* =========================================================
   REACT SELECT STYLE
========================================================= */

function getTruckSelectStyles(
  hasError
) {
  const isLightTheme =
    typeof document !==
      "undefined" &&
    Boolean(
      document.querySelector(
        '[data-theme="light"]'
      )
    );

  const colors =
    isLightTheme
      ? {
          text: "#172033",
          placeholder: "#8b9bae",

          indicator: "#71849b",
          indicatorFocused:
            "#0878ed",
          indicatorHover:
            "#0878ed",

          separator:
            "rgba(69, 94, 126, 0.17)",

          menuBackground:
            "#ffffff",
          menuBorder:
            "#cbd8e6",
          menuShadow:
            "0 18px 42px rgba(45, 78, 119, 0.16)",

          optionText:
            "#34485f",
          optionSelectedText:
            "#ffffff",

          optionSelectedBackground:
            "#1688ff",

          optionFocusedBackground:
            "#edf6ff",

          optionActiveBackground:
            "#dceeff",

          clear:
            "#71849b",

          clearHover:
            "#c33a45",
        }
      : {
          text: "#f1f3f7",
          placeholder: "#747b87",

          indicator: "#858d99",
          indicatorFocused:
            "#b8c3d3",
          indicatorHover:
            "#d8dde5",

          separator:
            "rgba(255, 255, 255, 0.1)",

          menuBackground:
            "#111318",
          menuBorder:
            "rgba(255, 255, 255, 0.1)",
          menuShadow:
            "0 20px 48px rgba(0, 0, 0, 0.48)",

          optionText:
            "#c8ced8",
          optionSelectedText:
            "#ffffff",

          optionSelectedBackground:
            "rgba(38, 116, 231, 0.28)",

          optionFocusedBackground:
            "rgba(255, 255, 255, 0.055)",

          optionActiveBackground:
            "rgba(38, 116, 231, 0.22)",

          clear:
            "#757d89",

          clearHover:
            "#e88d96",
        };

  return {
    ...customSelectStyles,

    container: (base) => ({
      ...base,
      width: "100%",
    }),

    control: (
      base,
      state
    ) => ({
      ...base,

      width: "100%",
      minHeight: 25,
      height: 25,
      padding: 0,

      border: "none",
      borderRadius: 0,

      backgroundColor:
        "transparent",

      boxShadow: "none",

      cursor: "pointer",

      ":hover": {
        border: "none",
      },
    }),

    valueContainer: (
      base
    ) => ({
      ...base,

      height: 25,
      padding: 0,
    }),

    input: (base) => ({
      ...base,

      margin: 0,
      padding: 0,

      color:
        colors.text,
    }),

    singleValue: (
      base
    ) => ({
      ...base,

      margin: 0,

      color:
        colors.text,

      fontSize: 12,
      fontWeight: 650,
    }),

    placeholder: (
      base
    ) => ({
      ...base,

      margin: 0,

      color:
        colors.placeholder,

      fontSize: 12,
    }),

    indicatorsContainer: (
      base
    ) => ({
      ...base,

      height: 25,

      display: "flex",

      alignItems:
        "center",

      transform:
        "translateY(-3px)",
    }),

    indicatorSeparator: (
      base
    ) => ({
      ...base,

      height: 18,

      marginTop: 0,
      marginBottom: 0,

      alignSelf:
        "center",

      backgroundColor:
        colors.separator,
    }),

    dropdownIndicator: (
      base,
      state
    ) => ({
      ...base,

      height: 25,

      padding:
        "0 6px",

      display:
        "flex",

      alignItems:
        "center",

      justifyContent:
        "center",

      color:
        state.isFocused
          ? colors.indicatorFocused
          : colors.indicator,

      ":hover": {
        color:
          colors.indicatorHover,
      },
    }),

    clearIndicator: (
      base
    ) => ({
      ...base,

      height: 25,

      padding:
        "0 6px",

      display:
        "flex",

      alignItems:
        "center",

      justifyContent:
        "center",

      color:
        colors.clear,

      ":hover": {
        color:
          colors.clearHover,
      },
    }),

    menu: (base) => ({
      ...base,

      zIndex: 9999,

      marginTop: 8,

      overflow:
        "hidden",

      border:
        `1px solid ${colors.menuBorder}`,

      borderRadius: 10,

      backgroundColor:
        colors.menuBackground,

      boxShadow:
        colors.menuShadow,
    }),

    menuPortal: (
      base
    ) => ({
      ...base,

      zIndex: 99999,
    }),

    menuList: (
      base
    ) => ({
      ...base,

      padding: 5,

      maxHeight: 250,

      backgroundColor:
        colors.menuBackground,
    }),

    option: (
      base,
      state
    ) => ({
      ...base,

      margin:
        "2px 0",

      padding:
        "8px 10px",

      borderRadius: 7,

      cursor:
        "pointer",

      color:
        state.isSelected
          ? colors.optionSelectedText
          : colors.optionText,

      backgroundColor:
        state.isSelected
          ? colors.optionSelectedBackground
          : state.isFocused
          ? colors.optionFocusedBackground
          : colors.menuBackground,

      fontSize: 11,

      ":active": {
        backgroundColor:
          colors.optionActiveBackground,
      },
    }),

    noOptionsMessage: (
      base
    ) => ({
      ...base,

      color:
        colors.placeholder,

      backgroundColor:
        colors.menuBackground,
    }),
  };
}

/* =========================================================
   SMALL COMPONENTS
========================================================= */

function SectionHeading({
  number,
  title,
}) {
  return (
    <div className="edit-truck-sectionHeading">
      <h2>
        {number}. {title}
      </h2>
    </div>
  );
}

function FieldCard({
  label,
  icon,
  error,
  className = "",
  children,
}) {
  return (
    <div
      className={[
        "edit-truck-fieldCard",
        error ? "has-error" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <span className="edit-truck-fieldCard-icon">
        {icon}
      </span>

      <div className="edit-truck-fieldCard-body">
        <div className="edit-truck-fieldCard-label">
          {label}
        </div>

        <div className="edit-truck-fieldCard-control">
          {children}
        </div>

        {error ? (
          <div className="edit-truck-fieldCard-error">
            {error}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function DateInput({
  id,
  name,
  value,
  onChange,
  hasError,
}) {
  const inputRef =
    useRef(
      null
    );

  const openCalendar =
    () => {
      const input =
        inputRef.current;

      if (
        !input
      ) {
        return;
      }

      input.focus({
        preventScroll:
          true,
      });

      try {
        if (
          typeof input.showPicker ===
          "function"
        ) {
          input.showPicker();
          return;
        }
      } catch {
        // Fallback para browsers sem showPicker.
      }

      input.click();
    };

  return (
    <div
      className={[
        "edit-truck-dateControl",
        hasError
          ? "has-error"
          : "",
      ]
        .filter(
          Boolean
        )
        .join(
          " "
        )}
    >
      <input
        ref={inputRef}
        id={id}
        name={name}
        type="date"
        value={value || ""}
        onChange={onChange}
      />

      <button
        type="button"
        className="edit-truck-dateControl-button"
        aria-label="Abrir calendário"
        title="Abrir calendário"
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={
          openCalendar
        }
      >
        <FaCalendarAlt />
      </button>
    </div>
  );
}

function MonthControl({
  id,
  value,
  onChange,
  hasError,
}) {
  const emitChange = (
    nextValue
  ) => {
    onChange({
      target: {
        value:
          nextValue,
      },
    });
  };

  return (
    <div
      className={[
        "edit-truck-cardMonthControl",
        hasError
          ? "has-error"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        maxLength={7}
        placeholder="MM-YYYY"
        value={
          value || ""
        }
        onChange={(
          event
        ) => {
          emitChange(
            formatCardExpiryInput(
              event.target.value
            )
          );
        }}
      />

      <input
        type="month"
        className="edit-truck-cardMonthControl-nativePicker"
        value={
          cardExpiryToNativeMonth(
            value
          )
        }
        onChange={(
          event
        ) => {
          emitChange(
            nativeMonthToCardExpiry(
              event.target.value
            )
          );
        }}
        tabIndex={-1}
        aria-label="Selecionar mês de validade"
      />

      <span
        className="edit-truck-cardMonthControl-button"
        aria-hidden="true"
      >
        <FaCalendarAlt />
      </span>
    </div>
  );
}

function DocumentStatusPill({
  status,
}) {
  return (
    <span
      className={`edit-truck-documentStatus edit-truck-documentStatus--${status.tone}`}
    >
      {status.label}
    </span>
  );
}

function SidebarDocumentRow({
  title,
  status,
}) {
  return (
    <div className="edit-truck-sidebarDocumentRow">
      <div className="edit-truck-sidebarDocumentName">
        <FaFileAlt />

        <span>
          {title}
        </span>
      </div>

      <DocumentStatusPill
        status={
          status
        }
      />
    </div>
  );
}

function formatCardExpiryInput(
  value
) {
  const digits =
    String(
      value || ""
    )
      .replace(
        /\D/g,
        ""
      )
      .slice(
        0,
        6
      );

  if (
    digits.length <= 2
  ) {
    return digits;
  }

  return `${digits.slice(
    0,
    2
  )}-${digits.slice(
    2
  )}`;
}

function cardExpiryToNativeMonth(
  value
) {
  const raw =
    String(
      value || ""
    ).trim();

  const match =
    raw.match(
      /^(0[1-9]|1[0-2])-(\d{4})$/
    );

  if (
    !match
  ) {
    return "";
  }

  return `${match[2]}-${match[1]}`;
}

function nativeMonthToCardExpiry(
  value
) {
  const raw =
    String(
      value || ""
    ).trim();

  const match =
    raw.match(
      /^(\d{4})-(0[1-9]|1[0-2])$/
    );

  if (
    !match
  ) {
    return "";
  }

  return `${match[2]}-${match[1]}`;
}

function DocumentCard({
  fieldName,
  title,
  file,
  expiryField,
  expiryValue,
  status,
  invalidFile,
  invalidExpiry,
  onFileChange,
  onDateChange,
  onView,
  t,
}) {
  const inputId =
    `edit-truck-file-${fieldName}`;

  const fileName =
    niceExistingFileName(
      file
    );

  return (
    <article
      className={[
        "edit-truck-documentCard",
        invalidFile
          ? "has-error"
          : "",
      ]
        .filter(
          Boolean
        )
        .join(
          " "
        )}
    >
      <header className="edit-truck-documentCard-header">
        <div className="edit-truck-documentCard-identity">
          <span className="edit-truck-documentCard-icon">
            <FaFileAlt />
          </span>

          <div className="edit-truck-documentCard-titleWrap">
            <h3>
              {title}
            </h3>

            <span
              className="edit-truck-documentCard-fileName"
              title={
                fileName ||
                t(
                  "upload.no_file",
                  {
                    defaultValue:
                      "Sem ficheiro",
                  }
                )
              }
            >
              {fileName ||
                t(
                  "upload.no_file",
                  {
                    defaultValue:
                      "Sem ficheiro",
                  }
                )}
            </span>
          </div>
        </div>

        <div className="edit-truck-documentCard-actions">
          <button
            type="button"
            className="edit-truck-documentCard-change"
            onClick={() => {
              document
                .getElementById(
                  inputId
                )
                ?.click();
            }}
          >
            {file
              ? t(
                  "buttons.change",
                  {
                    defaultValue:
                      "Alterar",
                  }
                )
              : t(
                  "buttons.upload",
                  {
                    defaultValue:
                      "Carregar",
                  }
                )}
          </button>
        </div>
      </header>

      <div className="edit-truck-documentCard-middle">
        <DocumentStatusPill
          status={
            status
          }
        />

        {file ? (
          <button
            type="button"
            className="edit-truck-documentCard-fileLink"
            onClick={() => {
              onView(
                file,
                fileName ||
                  title
              );
            }}
            title={
              fileName
            }
          >
            <FaFileAlt />

            <span>
              {fileName}
            </span>
          </button>
        ) : (
          <button
            type="button"
            className="edit-truck-documentCard-empty"
            onClick={() => {
              document
                .getElementById(
                  inputId
                )
                ?.click();
            }}
          >
            <FaUpload />

            <span>
              {t(
                "upload.prompt",
                {
                  doc:
                    title,
                  defaultValue:
                    "Carregar documento",
                }
              )}
            </span>
          </button>
        )}
      </div>

      <div className="edit-truck-documentCard-divider" />

      {expiryField ? (
        <div className="edit-truck-documentCard-validity">
          <span>
            {t(
              "labels.expiry_date",
              {
                defaultValue:
                  "Data de Validade",
              }
            )}
          </span>

          <DateInput
            id={`edit-truck-date-${expiryField}`}
            name={
              expiryField
            }
            value={
              expiryValue
            }
            onChange={
              onDateChange
            }
            hasError={
              Boolean(
                invalidExpiry
              )
            }
          />
        </div>
      ) : (
        <div className="edit-truck-documentCard-validity">
          <span>
            {t(
              "labels.expiry_short",
              {
                defaultValue: "Validade",
              }
            )}
          </span>

          <div className="edit-truck-documentCard-emptyDate">
            <FaCalendarAlt />
            <strong>—</strong>
          </div>
        </div>
      )}

      {invalidFile ? (
        <div className="edit-truck-documentCard-error">
          {invalidFile}
        </div>
      ) : null}

      {invalidExpiry ? (
        <div className="edit-truck-documentCard-error">
          {invalidExpiry}
        </div>
      ) : null}

      <input
        type="file"
        id={inputId}
        name={fieldName}
        accept="image/*,application/pdf"
        onChange={
          onFileChange
        }
        className="edit-truck-hiddenFileInput"
      />
    </article>
  );
}

/* =========================================================
   COMPONENT
========================================================= */

function EditTruck() {
  const {
    t,
    i18n,
  } =
    useTranslation(
      "edit_truck"
    );

  const {
    truckId,
  } =
    useParams();

  const navigate =
    useNavigate();

  const location =
    useLocation();

  const prefill =
    location.state
      ?.prefill ||
    null;

  /*
   * Leitura síncrona do cache.
   * O primeiro render já nasce preenchido.
   */
  const cachedTruck =
    useMemo(
      () =>
        readTruckDetailsCache(
          truckId
        ),
      [
        truckId,
      ]
    );

  /*
   * O prefill vindo da lista pode ser parcial.
   * Valores vazios não apagam o conteúdo completo do cache.
   */
  const seed =
    useMemo(
      () =>
        mergeTruckDetailsData(
          cachedTruck,
          prefill
        ),
      [
        cachedTruck,
        prefill,
      ]
    );

  const hasInitialData =
    Boolean(
      seed &&
        Object.keys(
          seed
        ).length &&
        isTruckDetailsCacheFresh(
          truckId
        )
    );

  const [
    truck,
    setTruck,
  ] =
    useState(
      () => ({
        ...EMPTY_TRUCK,
        ...mapSourceToTruck(
          seed ||
            {}
        ),
      })
    );

  const [
    files,
    setFiles,
  ] =
    useState(
      () => ({
        ...EMPTY_FILES,
        ...mapSourceToFiles(
          seed ||
            {}
        ),
      })
    );

  const [
    cards,
    setCards,
  ] =
    useState(
      () =>
        mapSourceToCards(
          seed?.cards
        )
    );

  const [
    visibleCardPins,
    setVisibleCardPins,
  ] =
    useState(
      {}
    );

  const [
    invalidFields,
    setInvalidFields,
  ] =
    useState(
      {}
    );

  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState(
      ""
    );

  const [
    showSuccessPopup,
    setShowSuccessPopup,
  ] =
    useState(
      false
    );

  const [
    isSaving,
    setIsSaving,
  ] =
    useState(
      false
    );

  const [
    isInitialHydrating,
    setIsInitialHydrating,
  ] =
    useState(
      !hasInitialData
    );

  const [
    viewerFile,
    setViewerFile,
  ] =
    useState(
      null
    );

  const [
    previewPulse,
    setPreviewPulse,
  ] =
    useState(
      false
    );

  const truckDirtyRef =
    useRef(
      false
    );

  const filesDirtyRef =
    useRef(
      false
    );

  const cardsDirtyRef =
    useRef(
      false
    );

  const sidebarRef =
    useRef(
      null
    );

  const previewTimerRef =
    useRef(
      null
    );

  const navigationTimerRef =
    useRef(
      null
    );

  /*
   * Guarda imediatamente qualquer prefill recebido
   * pela navegação, antes do GET individual terminar.
   */
  useEffect(() => {
    if (
      !prefill
    ) {
      return;
    }

    primeTruckDetailsCache(
      truckId,
      prefill
    );
  }, [
    truckId,
    prefill,
  ]);

  useEffect(() => {
    return () => {
      if (
        previewTimerRef.current
      ) {
        window.clearTimeout(
          previewTimerRef.current
        );
      }

      if (
        navigationTimerRef.current
      ) {
        window.clearTimeout(
          navigationTimerRef.current
        );
      }
    };
  }, []);

  const gasOptions =
    useMemo(
      () =>
        GAS_VALUES.map(
          (
            value
          ) => ({
            value,

            label:
              t(
                `gas.${value}`,
                {
                  defaultValue:
                    value,
                }
              ),
          })
        ),
      [
        t,
      ]
    );

  const monthOptions =
    useMemo(
      () =>
        Array.from(
          {
            length:
              12,
          },
          (
            _,
            index
          ) => {
            const value =
              index +
              1;

            const date =
              new Date(
                2026,
                index,
                1
              );

            let label =
              new Intl.DateTimeFormat(
                i18n.language ||
                  "pt-PT",
                {
                  month:
                    "long",
                }
              ).format(
                date
              );

            label =
              label.charAt(
                0
              ).toUpperCase() +
              label.slice(
                1
              );

            return {
              value,
              label,
            };
          }
        ),
      [
        i18n.language,
      ]
    );

  const currentYear =
    new Date().getFullYear();

  const yearOptions =
    useMemo(
      () =>
        Array.from(
          {
            length:
              currentYear -
              1980 +
              1,
          },
          (
            _,
            index
          ) => {
            const year =
              currentYear -
              index;

            return {
              value:
                year,

              label:
                String(
                  year
                ),
            };
          }
        ),
      [
        currentYear,
      ]
    );

  const clearFieldError =
    useCallback(
      (
        field
      ) => {
        setInvalidFields(
          (
            previous
          ) => {
            if (
              !previous[
                field
              ]
            ) {
              return previous;
            }

            const next = {
              ...previous,
            };

            delete next[
              field
            ];

            return next;
          }
        );
      },
      []
    );

  const updateTruckField =
    useCallback(
      (
        field,
        value
      ) => {
        truckDirtyRef.current =
          true;

        setTruck(
          (
            previous
          ) => ({
            ...previous,
            [field]:
              value,
          })
        );

        clearFieldError(
          field
        );

        setErrorMessage(
          ""
        );
      },
      [
        clearFieldError,
      ]
    );

  const handleChange =
    useCallback(
      (
        event
      ) => {
        const {
          name,
          value,
        } =
          event.target;

        updateTruckField(
          name,
          value
        );
      },
      [
        updateTruckField,
      ]
    );

  const handleFileChange =
    useCallback(
      (
        event
      ) => {
        const {
          name,
          files:
            uploadedFiles,
        } =
          event.target;

        const file =
          uploadedFiles?.[0] ||
          null;

        if (
          !file
        ) {
          return;
        }

        filesDirtyRef.current =
          true;

        setFiles(
          (
            previous
          ) => ({
            ...previous,
            [name]:
              file,
          })
        );

        clearFieldError(
          name
        );

        setErrorMessage(
          ""
        );
      },
      [
        clearFieldError,
      ]
    );

  const addCard =
    useCallback(
      () => {
        cardsDirtyRef.current =
          true;

        setCards(
          (
            previous
          ) => [
            ...previous,
            createEmptyTruckCard(),
          ]
        );
      },
      []
    );

  const removeCard =
    useCallback(
      (
        clientId
      ) => {
        cardsDirtyRef.current =
          true;

        setCards(
          (
            previous
          ) => {
            return previous.filter(
              (
                card
              ) => {
                return (
                  card.clientId !==
                  clientId
                );
              }
            );
          }
        );

        setVisibleCardPins(
          (
            previous
          ) => {
            const next = {
              ...previous,
            };

            delete next[
              clientId
            ];

            return next;
          }
        );

        setInvalidFields(
          (
            previous
          ) => {
            const next = {
              ...previous,
            };

            Object.keys(
              next
            )
              .filter(
                (
                  key
                ) => {
                  return key.startsWith(
                    `card:${clientId}:`
                  );
                }
              )
              .forEach(
                (
                  key
                ) => {
                  delete next[
                    key
                  ];
                }
              );

            return next;
          }
        );
      },
      []
    );

  const updateCard =
    useCallback(
      (
        clientId,
        field,
        value
      ) => {
        cardsDirtyRef.current =
          true;

        setCards(
          (
            previous
          ) => {
            return previous.map(
              (
                card
              ) => {
                if (
                  card.clientId !==
                  clientId
                ) {
                  return card;
                }

                return {
                  ...card,

                  [field]:
                    value,
                };
              }
            );
          }
        );

        clearFieldError(
          cardErrorKey(
            clientId,
            field
          )
        );

        setErrorMessage(
          ""
        );
      },
      [
        clearFieldError,
      ]
    );

  const toggleCardPin =
    useCallback(
      (
        clientId
      ) => {
        setVisibleCardPins(
          (
            previous
          ) => ({
            ...previous,

            [clientId]:
              !previous[
                clientId
              ],
          })
        );
      },
      []
    );

  const checkUniqueness =
    useCallback(
      async (
        field,
        value
      ) => {
        try {
          const response =
            await axios.post(
              "/trucks/check-unique",
              {
                field,
                value,

                currentTruckId:
                  Number(
                    truckId
                  ),
              }
            );

          return Boolean(
            response.data
              ?.exists
          );
        } catch (
          error
        ) {
          console.error(
            "[EditTruck] uniqueness check failed:",
            error
          );

          return false;
        }
      },
      [
        truckId,
      ]
    );

  /*
   * GET individual executado em background.
   * Não limpamos o cache nem os campos antes do pedido.
   */
  useEffect(() => {
    const controller =
      new AbortController();

    let alive =
      true;

    const fetchTruck =
      async () => {
        try {
          const {
            data,
          } =
            await axios.get(
              `/trucks/${truckId}`,
              {
                signal:
                  controller.signal,

                params: {
                  _filesFresh:
                    Date.now(),
                },

                headers: {
                  "Cache-Control":
                    "no-cache",

                  Pragma:
                    "no-cache",
                },
              }
            );

          if (
            !alive
          ) {
            return;
          }

          writeTruckDetailsCache(
            truckId,
            data
          );

          const incomingTruck =
            mapSourceToTruck(
              data
            );

          const incomingFiles =
            mapSourceToFiles(
              data
            );

          setTruck(
            (
              previous
            ) => {
              if (
                !truckDirtyRef.current
              ) {
                return {
                  ...EMPTY_TRUCK,
                  ...incomingTruck,
                };
              }

              return fillOnlyMissingTruckFields(
                previous,
                incomingTruck
              );
            }
          );

          setFiles(
            (
              previous
            ) => {
              if (
                !filesDirtyRef.current
              ) {
                return {
                  ...EMPTY_FILES,
                  ...incomingFiles,
                };
              }

              return {
                InspectionPhoto:
                  isNewBrowserFile(
                    previous.InspectionPhoto
                  )
                    ? previous.InspectionPhoto
                    : incomingFiles.InspectionPhoto ??
                      previous.InspectionPhoto,

                VehicleRegistrationCertificate:
                  isNewBrowserFile(
                    previous.VehicleRegistrationCertificate
                  )
                    ? previous.VehicleRegistrationCertificate
                    : incomingFiles.VehicleRegistrationCertificate ??
                      previous.VehicleRegistrationCertificate,

                CommunityLicence:
                  isNewBrowserFile(
                    previous.CommunityLicence
                  )
                    ? previous.CommunityLicence
                    : incomingFiles.CommunityLicence ??
                      previous.CommunityLicence,

                TruckInsurance:
                  isNewBrowserFile(
                    previous.TruckInsurance
                  )
                    ? previous.TruckInsurance
                    : incomingFiles.TruckInsurance ??
                      previous.TruckInsurance,
              };
            }
          );

          try {
            const cardsResponse =
              await axios.get(
                `/trucks/${truckId}/cards`,
                {
                  signal:
                    controller.signal,

                  headers: {
                    "Cache-Control":
                      "no-cache",

                    Pragma:
                      "no-cache",
                  },
                }
              );

            if (
              alive &&
              !cardsDirtyRef.current
            ) {
              setCards(
                mapSourceToCards(
                  cardsResponse.data
                )
              );
            }
          } catch (
            cardsError
          ) {
            if (
              cardsError?.name ===
                "CanceledError" ||
              cardsError?.code ===
                "ERR_CANCELED"
            ) {
              return;
            }

            console.error(
              "[EditTruck] error fetching cards:",
              cardsError
            );

            if (
              alive
            ) {
              setErrorMessage(
                t(
                  "errors.cards_load_fail",
                  {
                    defaultValue:
                      "Falha ao carregar os cartões do camião.",
                  }
                )
              );
            }
          }
        } catch (
          error
        ) {
          if (
            error?.name ===
              "CanceledError" ||
            error?.code ===
              "ERR_CANCELED"
          ) {
            return;
          }

          console.error(
            "[EditTruck] error fetching truck:",
            error
          );

          if (
            !hasInitialData
          ) {
            setErrorMessage(
              t(
                "errors.load_fail",
                {
                  defaultValue:
                    "Falha ao carregar dados do camião.",
                }
              )
            );
          }
        } finally {
          if (
            alive
          ) {
            setIsInitialHydrating(
              false
            );
          }
        }
      };

    fetchTruck();

    return () => {
      alive =
        false;

      controller.abort();
    };
  }, [
    truckId,
    t,
    hasInitialData,
  ]);

  /*
   * Validação de unicidade do VIN.
   */
  useEffect(() => {
    const value =
      String(
        truck.vin ||
          ""
      ).trim();

    if (
      !value
    ) {
      return undefined;
    }

    const timeout =
      window.setTimeout(
        async () => {
          const exists =
            await checkUniqueness(
              "vin",
              value
            );

          setInvalidFields(
            (
              previous
            ) => ({
              ...previous,

              vin:
                exists
                  ? t(
                      "errors.unique.vin",
                      {
                        defaultValue:
                          "O VIN já existe",
                      }
                    )
                  : false,
            })
          );
        },
        400
      );

    return () => {
      window.clearTimeout(
        timeout
      );
    };
  }, [
    truck.vin,
    checkUniqueness,
    t,
  ]);

  /*
   * Validação de unicidade da matrícula.
   */
  useEffect(() => {
    const value =
      String(
        truck.plate ||
          ""
      ).trim();

    if (
      !value
    ) {
      return undefined;
    }

    const timeout =
      window.setTimeout(
        async () => {
          const exists =
            await checkUniqueness(
              "plate",
              value
            );

          setInvalidFields(
            (
              previous
            ) => ({
              ...previous,

              plate:
                exists
                  ? t(
                      "errors.unique.plate",
                      {
                        defaultValue:
                          "A matrícula já existe",
                      }
                    )
                  : false,
            })
          );
        },
        400
      );

    return () => {
      window.clearTimeout(
        timeout
      );
    };
  }, [
    truck.plate,
    checkUniqueness,
    t,
  ]);

  const validateFields =
    useCallback(
      () => {
        const nextErrors = {};

        const cleanVin =
          String(
            truck.vin ||
              ""
          ).trim();

        if (
          cleanVin !==
            "" &&
          cleanVin.length !==
            17
        ) {
          nextErrors.vin =
            t(
              "errors.vin_length",
              {
                defaultValue:
                  "O VIN deve ter exatamente 17 caracteres.",
              }
            );
        }

        if (
          invalidFields.vin ===
          t(
            "errors.unique.vin",
            {
              defaultValue:
                "O VIN já existe",
            }
          )
        ) {
          nextErrors.vin =
            invalidFields.vin;
        }

        if (
          invalidFields.plate ===
          t(
            "errors.unique.plate",
            {
              defaultValue:
                "A matrícula já existe",
            }
          )
        ) {
          nextErrors.plate =
            invalidFields.plate;
        }

        cards.forEach(
          (
            card
          ) => {
            if (
              !hasCardValue(
                card
              )
            ) {
              return;
            }

            if (
              !String(
                card.provider ||
                  ""
              ).trim()
            ) {
              nextErrors[
                cardErrorKey(
                  card.clientId,
                  "provider"
                )
              ] =
                t(
                  "errors.card_provider_required",
                  {
                    defaultValue:
                      "Indique o fornecedor do cartão.",
                  }
                );
            }

            /*
             * Só cartões novos precisam
             * obrigatoriamente do número.
             *
             * Um cartão existente já tem
             * o número cifrado no servidor.
             */
            if (
              !card.id &&
              !String(
                card.cardNumber ||
                  ""
              ).trim()
            ) {
              nextErrors[
                cardErrorKey(
                  card.clientId,
                  "cardNumber"
                )
              ] =
                t(
                  "errors.card_number_required",
                  {
                    defaultValue:
                      "Indique o número do cartão.",
                  }
                );
            }
          }
        );

        setInvalidFields(
          nextErrors
        );

        return (
          Object.keys(
            nextErrors
          ).length ===
          0
        );
      },
      [
        truck.vin,
        invalidFields.vin,
        invalidFields.plate,
        cards,
        t,
      ]
    );

  const handleSubmit =
    async (
      event
    ) => {
      event.preventDefault();

      if (
        isSaving
      ) {
        return;
      }

      setErrorMessage(
        ""
      );

      setShowSuccessPopup(
        false
      );

      const valid =
        validateFields();

      if (
        !valid
      ) {
        setErrorMessage(
          t(
            "errors.fill_all",
            {
              defaultValue:
                "Verifica os campos assinalados.",
            }
          )
        );

        return;
      }

      const formData =
        new FormData();

      formData.append(
        "vin",
        truck.vin ??
          ""
      );

      formData.append(
        "type_of_gas",
        truck.type_of_gas ??
          ""
      );

      formData.append(
        "brand",
        truck.brand ??
          ""
      );

      formData.append(
        "model",
        truck.model ??
          ""
      );

      formData.append(
        "plate",
        truck.plate ??
          ""
      );

      formData.append(
        "Month",
        truck.month ??
          ""
      );

      formData.append(
        "year",
        truck.year ??
          ""
      );

      formData.append(
        "number_of_straps",
        truck.number_of_straps ??
          ""
      );

      formData.append(
        "number_of_ratchets",
        truck.number_of_ratchets ??
          ""
      );

      formData.append(
        "number_of_edge_protectors",
        truck.number_of_edge_protectors ??
          ""
      );

      formData.append(
        "number_of_anti_slip_mats",
        truck.number_of_anti_slip_mats ??
          ""
      );

      formData.append(
        "InspectionPhotoExpiry",
        truck.InspectionPhotoExpiry ??
          ""
      );

      formData.append(
        "TruckInsuranceExpiry",
        truck.TruckInsuranceExpiry ??
          ""
      );

      formData.append(
        "CommunityLicenceExpiry",
        truck.CommunityLicenceExpiry ??
          ""
      );

      const cardsToSubmit =
        cards
          .filter(
            hasCardValue
          )
          .map(
            (
              card
            ) => {
              const payload = {
                provider:
                  String(
                    card.provider ||
                      ""
                  ).trim(),

                expiryMonth:
                  card.expiryMonth ||
                  "",
              };

              if (
                card.id
              ) {
                payload.id =
                  card.id;
              }

              const newCardNumber =
                String(
                  card.cardNumber ||
                    ""
                ).trim();

              if (
                newCardNumber
              ) {
                payload.cardNumber =
                  newCardNumber;
              }

              const newPin =
                String(
                  card.pin ||
                    ""
                ).trim();

              if (
                newPin
              ) {
                payload.pin =
                  newPin;
              }

              return payload;
            }
          );

      formData.append(
        "cards",
        JSON.stringify(
          cardsToSubmit
        )
      );

      if (
        isNewBrowserFile(
          files.InspectionPhoto
        )
      ) {
        formData.append(
          "InspectionPhoto",
          files.InspectionPhoto
        );
      }

      if (
        isNewBrowserFile(
          files.VehicleRegistrationCertificate
        )
      ) {
        formData.append(
          "VehicleRegistrationCertificate",
          files.VehicleRegistrationCertificate
        );
      }

      if (
        isNewBrowserFile(
          files.CommunityLicence
        )
      ) {
        formData.append(
          "CommunityLicence",
          files.CommunityLicence
        );
      }

      if (
        isNewBrowserFile(
          files.TruckInsurance
        )
      ) {
        formData.append(
          "TruckInsurance",
          files.TruckInsurance
        );
      }

      setIsSaving(
        true
      );

      try {
        const response =
          await axios.put(
            `/trucks/${truckId}`,
            formData,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );

        const responseTruck =
          response?.data &&
          typeof response.data ===
            "object" &&
          (
            response.data.id ||
            response.data.vin ||
            response.data.plate
          )
            ? response.data
            : null;

        if (
          responseTruck
        ) {
          writeTruckDetailsCache(
            truckId,
            responseTruck
          );
        } else {
          primeTruckDetailsCache(
            truckId,
            {
              ...(
                seed ||
                {}
              ),

              ...truck,

              Month:
                truck.month,
            }
          );
        }

        /*
         * Atualizamos o cache com a resposta completa
         * para incluir também URLs dos novos documentos.
         */
        try {
          const freshResponse =
            await axios.get(
              `/trucks/${truckId}`,
              {
                params: {
                  _filesFresh:
                    Date.now(),
                },

                headers: {
                  "Cache-Control":
                    "no-cache",

                  Pragma:
                    "no-cache",
                },
              }
            );

          writeTruckDetailsCache(
            truckId,
            freshResponse.data
          );

          const freshCardsResponse =
            await axios.get(
              `/trucks/${truckId}/cards`,
              {
                headers: {
                  "Cache-Control":
                    "no-cache",

                  Pragma:
                    "no-cache",
                },
              }
            );

          cardsDirtyRef.current =
            false;

          setCards(
            mapSourceToCards(
              freshCardsResponse.data
            )
          );
        } catch (
          refreshError
        ) {
          console.warn(
            "[EditTruck] truck saved but cache refresh failed:",
            refreshError
          );
        }

        setShowSuccessPopup(
          true
        );

        navigationTimerRef.current =
          window.setTimeout(
            () => {
              navigate(
                "/trucks"
              );
            },
            1200
          );
      } catch (
        error
      ) {
        console.error(
          "[EditTruck] error updating truck:",
          error
        );

        console.error(
          "[EditTruck] update error response:",
          error.response
            ?.data
        );

        const backendErrors =
          error.response
            ?.data
            ?.errors;

        if (
          Array.isArray(
            backendErrors
          )
        ) {
          const vinError =
            backendErrors.find(
              (
                item
              ) =>
                item.param ===
                "vin"
            );

          if (
            vinError
          ) {
            setInvalidFields(
              {
                vin:
                  vinError.msg,
              }
            );

            setErrorMessage(
              vinError.msg
            );

            return;
          }
        }

        setErrorMessage(
          error.response
            ?.data
            ?.message ||
            error.response
              ?.data
              ?.error ||
            t(
              "errors.update_fail",
              {
                defaultValue:
                  "Falha ao atualizar o camião. Tenta novamente.",
              }
            )
        );
      } finally {
        setIsSaving(
          false
        );
      }
    };

  const viewerData =
    useMemo(
      () => {
        if (
          !viewerFile
            ?.file
        ) {
          return null;
        }

        const href =
          buildFileUrl(
            viewerFile.file
          );

        const name =
          safeDownloadFileName(
            viewerFile.title ||
              niceExistingFileName(
                viewerFile.file
              ),
            t(
              "upload.view_file",
              {
                defaultValue:
                  "Ver ficheiro",
              }
            )
          );

        return {
          href,
          name,

          file:
            viewerFile.file,

          isPdf:
            isPdfFile(
              viewerFile.file
            ),

          isImage:
            isImageFile(
              viewerFile.file
            ),
        };
      },
      [
        viewerFile,
        t,
      ]
    );

  useEffect(() => {
    return () => {
      if (
        viewerData
          ?.href
          ?.startsWith(
            "blob:"
          )
      ) {
        try {
          URL.revokeObjectURL(
            viewerData.href
          );
        } catch {
          // URL já revogado.
        }
      }
    };
  }, [
    viewerData?.href,
  ]);

  useEffect(() => {
    if (
      !viewerData
    ) {
      return undefined;
    }

    const handleKeyDown =
      (
        event
      ) => {
        if (
          event.key ===
          "Escape"
        ) {
          setViewerFile(
            null
          );
        }
      };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    const previousOverflow =
      document.body.style
        .overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );

      document.body.style.overflow =
        previousOverflow;
    };
  }, [
    viewerData,
  ]);

  const handleDownloadViewerFile =
    async () => {
      if (
        !viewerData
          ?.href
      ) {
        return;
      }

      const fallbackName =
        safeDownloadFileName(
          viewerData.name ||
            niceExistingFileName(
              viewerData.file
            ),
          "document"
        );

      try {
        let blob;
        let contentType =
          "";

        if (
          typeof File !==
            "undefined" &&
          (
            viewerData.file instanceof File ||
            viewerData.file instanceof Blob
          )
        ) {
          blob =
            viewerData.file;

          contentType =
            viewerData.file
              .type ||
            "";
        } else {
          const response =
            await axios.get(
              "/company-details/download-file",
              {
                responseType:
                  "blob",

                params: {
                  url:
                    viewerData.href,

                  filename:
                    fallbackName,
                },
              }
            );

          blob =
            response.data;

          contentType =
            String(
              response.headers?.[
                "content-type"
              ] ||
                blob?.type ||
                ""
            );
        }

        const lowerType =
          contentType.toLowerCase();

        if (
          lowerType.includes(
            "application/json"
          ) ||
          lowerType.includes(
            "text/html"
          ) ||
          lowerType.includes(
            "application/xml"
          ) ||
          lowerType.includes(
            "text/xml"
          )
        ) {
          let errorText =
            "";

          try {
            errorText =
              await blob.text();
          } catch {
            // Ignorar erro de leitura.
          }

          console.error(
            "[EditTruck] invalid download response:",
            {
              contentType,
              errorText,
            }
          );

          window.alert(
            "O servidor devolveu uma resposta inválida em vez do ficheiro."
          );

          return;
        }

        if (
          !blob ||
          blob.size ===
            0
        ) {
          window.alert(
            "O ficheiro descarregado está vazio."
          );

          return;
        }

        const blobUrl =
          window.URL.createObjectURL(
            blob
          );

        const link =
          document.createElement(
            "a"
          );

        link.href =
          blobUrl;

        link.download =
          fallbackName;

        link.style.display =
          "none";

        document.body.appendChild(
          link
        );

        link.click();
        link.remove();

        window.setTimeout(
          () => {
            window.URL.revokeObjectURL(
              blobUrl
            );
          },
          1000
        );
      } catch (
        error
      ) {
        console.error(
          "[EditTruck] download failed:",
          error
        );

        try {
          const errorBlob =
            error?.response
              ?.data;

          if (
            errorBlob &&
            typeof errorBlob.text ===
              "function"
          ) {
            const text =
              await errorBlob.text();

            console.error(
              "[EditTruck] backend error body:",
              text
            );
          }
        } catch {
          // Ignorar erro secundário.
        }

        window.alert(
          "Não foi possível descarregar o ficheiro automaticamente."
        );
      }
    };

  const handlePreviewTruck =
    () => {
      sidebarRef.current?.scrollIntoView(
        {
          behavior:
            "smooth",

          block:
            "start",
        }
      );

      setPreviewPulse(
        true
      );

      if (
        previewTimerRef.current
      ) {
        window.clearTimeout(
          previewTimerRef.current
        );
      }

      previewTimerRef.current =
        window.setTimeout(
          () => {
            setPreviewPulse(
              false
            );
          },
          900
        );
    };

  const truckStatus =
    getTruckStatus(
      truck,
      t
    );

  const selectedMonth =
    monthOptions.find(
      (
        option
      ) =>
        Number(
          option.value
        ) ===
        Number(
          truck.month
        )
    ) ||
    null;

  const selectedYear =
    yearOptions.find(
      (
        option
      ) =>
        Number(
          option.value
        ) ===
        Number(
          truck.year
        )
    ) ||
    null;

  const selectedGas =
    gasOptions.find(
      (
        option
      ) =>
        option.value ===
        truck.type_of_gas
    ) ||
    (
      truck.type_of_gas
        ? {
            value:
              truck.type_of_gas,

            label:
              t(
                `gas.${truck.type_of_gas}`,
                {
                  defaultValue:
                    truck.type_of_gas,
                }
              ),
          }
        : null
    );

  const gasLabel =
    selectedGas
      ?.label ||
    "—";

  const monthLabel =
    selectedMonth
      ?.label ||
    "—";

  const truckBrandModel =
    [
      truck.brand,
      truck.model,
    ]
      .filter(
        Boolean
      )
      .join(
        " "
      ) ||
    "—";

  const documentItems =
    [
      {
        key:
          "InspectionPhoto",

        title:
          t(
            "labels.inspection_photo",
            {
              defaultValue:
                "Inspeção (Foto/Doc.)",
            }
          ),

        expiryField:
          "InspectionPhotoExpiry",

        expiryValue:
          truck.InspectionPhotoExpiry,
      },

      {
        key:
          "TruckInsurance",

        title:
          t(
            "labels.truck_insurance",
            {
              defaultValue:
                "Seguro do camião",
            }
          ),

        expiryField:
          "TruckInsuranceExpiry",

        expiryValue:
          truck.TruckInsuranceExpiry,
      },

      {
        key:
          "CommunityLicence",

        title:
          t(
            "labels.community_licence",
            {
              defaultValue:
                "Licença Comunitária",
            }
          ),

        expiryField:
          "CommunityLicenceExpiry",

        expiryValue:
          truck.CommunityLicenceExpiry,
      },

      {
        key:
          "VehicleRegistrationCertificate",

        title:
          t(
            "labels.vehicle_registration_certificate",
            {
              defaultValue:
                "Certificado de Matrícula",
            }
          ),

        expiryField:
          null,

        expiryValue:
          "",
      },
    ].map(
      (
        item
      ) => ({
        ...item,

        file:
          files[
            item.key
          ],

        status:
          getDocumentStatus(
            files[item.key],
            Boolean(item.expiryField),
            t
          ),
        })
    );

  const sidebarSpecs = [
    {
      icon: FaCalendarAlt,

      label: t(
        "labels.year",
        {
          defaultValue: "Ano",
        }
      ),

      value: truck.year || "—",
    },

    {
      icon: FaClock,

      label: t(
        "labels.month",
        {
          defaultValue: "Mês",
        }
      ),

      value: monthLabel,
    },

    {
      icon: FaLink,

      label: t(
        "labels.number_of_straps",
        {
          defaultValue: "N.º de Cintas",
        }
      ),

      value:
        truck.number_of_straps ||
        "0",
    },

    {
      icon: FaLink,

      label: t(
        "labels.number_of_ratchets",
        {
          defaultValue: "N.º de Catracas",
        }
      ),

      value:
        truck.number_of_ratchets ||
        "0",
    },

    {
      icon: FaShieldAlt,

      label: t(
        "labels.number_of_edge_protectors",
        {
          defaultValue:
            "N.º de Protetores de Canto",
        }
      ),

      value:
        truck.number_of_edge_protectors ||
        "0",
    },

    {
      icon: FaLayerGroup,

      label: t(
        "labels.number_of_anti_slip_mats",
        {
          defaultValue:
            "N.º de Tapetes Antiderrapantes",
        }
      ),

      value:
        truck.number_of_anti_slip_mats ||
        "0",
    },
  ];

  return (
    <div className="edit-truck-page">
      <div className="edit-truck-shell">
        <header className="edit-truck-topbar">
          <button
            type="button"
            className="edit-truck-backButton"
            onClick={() => {
              navigate(
                "/trucks"
              );
            }}
          >
            <FaArrowLeft />

            <span>
              {t(
                "back",
                {
                  defaultValue:
                    "Voltar",
                }
              )}
            </span>
          </button>

          <div className="edit-truck-topbar-title">
            <h1>
              {t(
                "title",
                {
                  defaultValue:
                    "Editar Camião",
                }
              )}
            </h1>

            <p>
              {t(
                "page.subtitle",
                {
                  defaultValue:
                    "Atualize a informação, documentos e registos do camião.",
                }
              )}
            </p>
          </div>

          <div className="edit-truck-topbar-actions">
            <button
              type="button"
              className="edit-truck-previewButton"
              onClick={
                handlePreviewTruck
              }
              disabled={
                isInitialHydrating
              }
            >
              <FaEye />

              <span>
                {t(
                  "buttons.preview",
                  {
                    defaultValue:
                      "Pré-visualizar",
                  }
                )}
              </span>
            </button>

            <button
              type="submit"
              form="edit-truck-form"
              className="edit-truck-primarySaveButton"
              disabled={
                isInitialHydrating ||
                isSaving
              }
            >
              <FaSave />

              <span>
                {isSaving
                  ? t(
                      "buttons.saving",
                      {
                        defaultValue:
                          "A guardar...",
                      }
                    )
                  : t(
                      "buttons.save_changes",
                      {
                        defaultValue:
                          "Guardar alterações",
                      }
                    )}
              </span>
            </button>
          </div>
        </header>

        {errorMessage ? (
          <div className="edit-truck-globalError">
            <FaExclamationTriangle />

            <span>
              {errorMessage}
            </span>
          </div>
        ) : null}

        {isInitialHydrating ? (
          <div className="edit-truck-skeletonLayout">
            <aside className="edit-truck-skeletonSidebar">
              <span />
              <span />
              <span />
              <span />
              <span />
            </aside>

            <main className="edit-truck-skeletonMain">
              <section>
                <span />
                <div>
                  {Array.from(
                    {
                      length:
                        10,
                    }
                  ).map(
                    (
                      _,
                      index
                    ) => (
                      <i
                        key={
                          index
                        }
                      />
                    )
                  )}
                </div>
              </section>

              <section>
                <span />
                <div>
                  {Array.from(
                    {
                      length:
                        4,
                    }
                  ).map(
                    (
                      _,
                      index
                    ) => (
                      <i
                        key={
                          index
                        }
                      />
                    )
                  )}
                </div>
              </section>
            </main>
          </div>
        ) : (
          <div className="edit-truck-layout">
            <aside
              ref={
                sidebarRef
              }
              className={[
                "edit-truck-sidebar",
                previewPulse
                  ? "is-previewing"
                  : "",
              ]
                .filter(
                  Boolean
                )
                .join(
                  " "
                )}
            >
              <div className="edit-truck-sidebarCard">
                <div className="edit-truck-sidebarIdentity">
                  <span className="edit-truck-sidebarEyebrow">
                    {t(
                      "labels.plate",
                      {
                        defaultValue:
                          "Matrícula",
                      }
                    )}
                  </span>

                  <h2>
                    {truck.plate ||
                      "—"}
                  </h2>

                  <p>
                    {truckBrandModel}
                  </p>

                  <span
                    className={[
                      "edit-truck-vehicleStatus",
                      truckStatus.active
                        ? "is-active"
                        : "is-inactive",
                    ]
                      .filter(
                        Boolean
                      )
                      .join(
                        " "
                      )}
                  >
                    <span />

                    {truckStatus.label}
                  </span>
                </div>

                <div className="edit-truck-sidebarBlock">
                  <div className="edit-truck-sidebarBlockTitle">
                    {t(
                      "sections.fuel",
                      {
                        defaultValue: "Combustível",
                      }
                    )}
                  </div>

                  <div className="edit-truck-sidebarFuel">
                    <FaGasPump />

                    <span>
                      {gasLabel}
                    </span>
                  </div>
                </div>

                <div className="edit-truck-sidebarBlock">
                  <div className="edit-truck-sidebarBlockTitle">
                    {t(
                      "sections.documents",
                      {
                        defaultValue:
                          "Documentos",
                      }
                    )}
                  </div>

                  <div className="edit-truck-sidebarDocuments">
                    {documentItems.map(
                      (
                        item
                      ) => (
                        <SidebarDocumentRow
                          key={
                            item.key
                          }
                          title={
                            item.title
                          }
                          status={
                            item.status
                          }
                        />
                      )
                    )}
                  </div>
                </div>

                <div className="edit-truck-sidebarBlock">
                  <div className="edit-truck-sidebarBlockTitle">
                    {t(
                      "sections.specifications",
                      {
                        defaultValue:
                          "Especificações",
                      }
                    )}
                  </div>

                  <div className="edit-truck-sidebarSpecs">
                    {sidebarSpecs.map(
                      (
                        item
                      ) => (
                        <div
                          key={
                            item.label
                          }
                          className="edit-truck-sidebarSpecRow"
                        >
                          <span className="edit-truck-sidebarSpecLabel">
                            <item.icon />

                            <span>
                              {item.label}
                            </span>
                          </span>

                          <strong>
                            {item.value}
                          </strong>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </aside>

            <main className="edit-truck-main">
              <form
                id="edit-truck-form"
                className="edit-truck-dashboardForm"
                onSubmit={
                  handleSubmit
                }
                encType="multipart/form-data"
              >
                <section className="edit-truck-sectionCard">
                  <SectionHeading
                    number="1"
                    title={t(
                      "sections.basic",
                      {
                        defaultValue:
                          "Informação Básica",
                      }
                    )}
                    description={t(
                      "sections.basic_description",
                      {
                        defaultValue:
                          "Identificação e equipamento principal do veículo.",
                      }
                    )}
                  />

                  <div className="edit-truck-basicGrid">
                    <FieldCard
                      label="VIN"
                      icon={
                        <FaHashtag />
                      }
                      error={
                        invalidFields.vin
                      }
                      className="edit-truck-span-7"
                    >
                      <input
                        name="vin"
                        value={
                          truck.vin ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        type="text"
                        className="edit-truck-controlInput"
                        placeholder={t(
                          "placeholders.vin",
                          {
                            defaultValue:
                              "Introduz o VIN...",
                          }
                        )}
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.type_of_gas",
                        {
                          defaultValue:
                            "Tipo de Combustível",
                        }
                      )}
                      icon={
                        <FaGasPump />
                      }
                      error={
                        invalidFields.type_of_gas
                      }
                      className="edit-truck-span-5"
                    >
                      <Select
                        name="type_of_gas"
                        value={
                          selectedGas
                        }
                        onChange={(
                          option
                        ) => {
                          updateTruckField(
                            "type_of_gas",
                            option
                              ?.value ||
                              ""
                          );
                        }}
                        options={
                          gasOptions
                        }
                        styles={getTruckSelectStyles(
                          Boolean(
                            invalidFields.type_of_gas
                          )
                        )}
                        className="edit-truck-select"
                        placeholder={t(
                          "placeholders.type_of_gas",
                          {
                            defaultValue:
                              "Seleciona o combustível",
                          }
                        )}
                        isClearable
                        menuPortalTarget={
                          typeof document !==
                            "undefined"
                            ? document.body
                            : null
                        }
                        menuPosition="fixed"
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.brand",
                        {
                          defaultValue:
                            "Marca",
                        }
                      )}
                      icon={
                        <FaIndustry />
                      }
                      error={
                        invalidFields.brand
                      }
                      className="edit-truck-span-4"
                    >
                      <input
                        name="brand"
                        value={
                          truck.brand ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        type="text"
                        className="edit-truck-controlInput"
                        placeholder={t(
                          "placeholders.brand",
                          {
                            defaultValue:
                              "Introduz a marca...",
                          }
                        )}
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.model",
                        {
                          defaultValue:
                            "Modelo",
                        }
                      )}
                      icon={
                        <FaCube />
                      }
                      error={
                        invalidFields.model
                      }
                      className="edit-truck-span-4"
                    >
                      <input
                        name="model"
                        value={
                          truck.model ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        type="text"
                        className="edit-truck-controlInput"
                        placeholder={t(
                          "placeholders.model",
                          {
                            defaultValue:
                              "Introduz o modelo...",
                          }
                        )}
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.plate",
                        {
                          defaultValue:
                            "Matrícula",
                        }
                      )}
                      icon={
                        <FaIdCard />
                      }
                      error={
                        invalidFields.plate
                      }
                      className="edit-truck-span-4"
                    >
                      <input
                        name="plate"
                        value={
                          truck.plate ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        type="text"
                        className="edit-truck-controlInput edit-truck-controlInput--uppercase"
                        placeholder={t(
                          "placeholders.plate",
                          {
                            defaultValue:
                              "Introduz a matrícula...",
                          }
                        )}
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.month",
                        {
                          defaultValue:
                            "Mês",
                        }
                      )}
                      icon={
                        <FaCalendarAlt />
                      }
                      error={
                        invalidFields.month
                      }
                      className="edit-truck-span-6"
                    >
                      <Select
                        name="month"
                        value={
                          selectedMonth
                        }
                        onChange={(
                          option
                        ) => {
                          updateTruckField(
                            "month",
                            option
                              ?.value ||
                              ""
                          );
                        }}
                        options={
                          monthOptions
                        }
                        styles={getTruckSelectStyles(
                          Boolean(
                            invalidFields.month
                          )
                        )}
                        placeholder={t(
                          "placeholders.month",
                          {
                            defaultValue:
                              "Seleciona o mês",
                          }
                        )}
                        isClearable
                        menuPortalTarget={
                          typeof document !==
                            "undefined"
                            ? document.body
                            : null
                        }
                        menuPosition="fixed"
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.year",
                        {
                          defaultValue:
                            "Ano",
                        }
                      )}
                      icon={
                        <FaCalendarAlt />
                      }
                      error={
                        invalidFields.year
                      }
                      className="edit-truck-span-6"
                    >
                      <Select
                        name="year"
                        value={
                          selectedYear
                        }
                        onChange={(
                          option
                        ) => {
                          updateTruckField(
                            "year",
                            option
                              ?.value ||
                              ""
                          );
                        }}
                        options={
                          yearOptions
                        }
                        styles={getTruckSelectStyles(
                          Boolean(
                            invalidFields.year
                          )
                        )}
                        placeholder={t(
                          "placeholders.year",
                          {
                            defaultValue:
                              "Seleciona o ano",
                          }
                        )}
                        isClearable
                        menuPortalTarget={
                          typeof document !==
                            "undefined"
                            ? document.body
                            : null
                        }
                        menuPosition="fixed"
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.number_of_straps",
                        {
                          defaultValue:
                            "N.º de Cintas",
                        }
                      )}
                      icon={
                        <FaLink />
                      }
                      error={
                        invalidFields.number_of_straps
                      }
                      className="edit-truck-span-3"
                    >
                      <input
                        name="number_of_straps"
                        value={
                          truck.number_of_straps ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        type="number"
                        min="0"
                        className="edit-truck-controlInput"
                        placeholder="0"
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.number_of_ratchets",
                        {
                          defaultValue:
                            "N.º de Catracas",
                        }
                      )}
                      icon={
                        <FaLink />
                      }
                      error={
                        invalidFields.number_of_ratchets
                      }
                      className="edit-truck-span-3"
                    >
                      <input
                        name="number_of_ratchets"
                        value={
                          truck.number_of_ratchets ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        type="number"
                        min="0"
                        className="edit-truck-controlInput"
                        placeholder="0"
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.number_of_edge_protectors",
                        {
                          defaultValue:
                            "N.º de Protetores de Canto",
                        }
                      )}
                      icon={
                        <FaShieldAlt />
                      }
                      error={
                        invalidFields.number_of_edge_protectors
                      }
                      className="edit-truck-span-3"
                    >
                      <input
                        name="number_of_edge_protectors"
                        value={
                          truck.number_of_edge_protectors ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        type="number"
                        min="0"
                        className="edit-truck-controlInput"
                        placeholder="0"
                      />
                    </FieldCard>

                    <FieldCard
                      label={t(
                        "labels.number_of_anti_slip_mats",
                        {
                          defaultValue:
                            "N.º de Tapetes Antiderrapantes",
                        }
                      )}
                      icon={
                        <FaLayerGroup />
                      }
                      error={
                        invalidFields.number_of_anti_slip_mats
                      }
                      className="edit-truck-span-3"
                    >
                      <input
                        name="number_of_anti_slip_mats"
                        value={
                          truck.number_of_anti_slip_mats ||
                          ""
                        }
                        onChange={
                          handleChange
                        }
                        type="number"
                        min="0"
                        className="edit-truck-controlInput"
                        placeholder="0"
                      />
                    </FieldCard>
                  </div>
                </section>

                <section className="edit-truck-sectionCard">
                  <SectionHeading
                    number="2"
                    title={t(
                      "sections.cards",
                      {
                        defaultValue:
                          "Cartões",
                      }
                    )}
                  />

                  <div className="edit-truck-cardsHeader">
                    <p className="edit-truck-cardsHint">
                      {t(
                        "cards.security_note",
                        {
                          defaultValue:
                            "Associe cartões de combustível, frota ou serviço ao camião. Os dados sensíveis existentes permanecem protegidos.",
                        }
                      )}
                    </p>

                    <button
                      type="button"
                      className="edit-truck-addCardButton"
                      onClick={
                        addCard
                      }
                    >
                      <FaPlus />

                      <span>
                        {t(
                          "buttons.add_card",
                          {
                            defaultValue:
                              "Adicionar cartão",
                          }
                        )}
                      </span>
                    </button>
                  </div>

                  {cards.length >
                  0 ? (
                    <div className="edit-truck-cardsList">
                      {cards.map(
                        (
                          card,
                          index
                        ) => {
                          const pinVisible =
                            Boolean(
                              visibleCardPins[
                                card.clientId
                              ]
                            );

                          const savedCardNumberPlaceholder =
                            card.id &&
                            card.cardLast4
                              ? `•••• ${card.cardLast4}`
                              : t(
                                  "placeholders.card_number",
                                  {
                                    defaultValue:
                                      "Introduza o número...",
                                  }
                                );

                          const savedPinPlaceholder =
                            card.id &&
                            card.hasPin
                              ? t(
                                  "placeholders.card_pin_saved",
                                  {
                                    defaultValue:
                                      "PIN/código guardado",
                                  }
                                )
                              : t(
                                  "placeholders.card_pin",
                                  {
                                    defaultValue:
                                      "Introduza o PIN/código...",
                                  }
                                );

                          return (
                            <article
                              key={
                                card.clientId
                              }
                              className="edit-truck-cardItem"
                            >
                              <header className="edit-truck-cardItemHeader">
                                <div className="edit-truck-cardItemTitle">
                                  <FaCreditCard />

                                  <span>
                                    {t(
                                      "cards.card_number",
                                      {
                                        number:
                                          index +
                                          1,

                                        defaultValue:
                                          `Cartão ${
                                            index +
                                            1
                                          }`,
                                      }
                                    )}
                                  </span>
                                </div>

                                <button
                                  type="button"
                                  className="edit-truck-removeCardButton"
                                  onClick={() => {
                                    removeCard(
                                      card.clientId
                                    );
                                  }}
                                  aria-label={t(
                                    "buttons.remove_card",
                                    {
                                      defaultValue:
                                        "Remover cartão",
                                    }
                                  )}
                                  title={t(
                                    "buttons.remove_card",
                                    {
                                      defaultValue:
                                        "Remover cartão",
                                    }
                                  )}
                                >
                                  <FaTrash />
                                </button>
                              </header>

                              <div className="edit-truck-cardGrid">
                                <FieldCard
                                  label={t(
                                    "labels.card_provider",
                                    {
                                      defaultValue:
                                        "Fornecedor",
                                    }
                                  )}
                                  icon={
                                    <FaBuilding />
                                  }
                                  error={
                                    invalidFields[
                                      cardErrorKey(
                                        card.clientId,
                                        "provider"
                                      )
                                    ]
                                  }
                                  className="edit-truck-cardSpan-3"
                                >
                                  <input
                                    type="text"
                                    value={
                                      card.provider
                                    }
                                    onChange={(
                                      event
                                    ) => {
                                      updateCard(
                                        card.clientId,
                                        "provider",
                                        event.target
                                          .value
                                      );
                                    }}
                                    className="edit-truck-controlInput"
                                    placeholder={t(
                                      "placeholders.card_provider",
                                      {
                                        defaultValue:
                                          "Ex.: DKV, UTA, Shell...",
                                      }
                                    )}
                                    maxLength={
                                      80
                                    }
                                    autoComplete="off"
                                  />
                                </FieldCard>

                                <FieldCard
                                  label={t(
                                    "labels.card_number",
                                    {
                                      defaultValue:
                                        "N.º do cartão",
                                    }
                                  )}
                                  icon={
                                    <FaCreditCard />
                                  }
                                  error={
                                    invalidFields[
                                      cardErrorKey(
                                        card.clientId,
                                        "cardNumber"
                                      )
                                    ]
                                  }
                                  className="edit-truck-cardSpan-4"
                                >
                                  <input
                                    type="text"
                                    value={
                                      card.cardNumber
                                    }
                                    onChange={(
                                      event
                                    ) => {
                                      updateCard(
                                        card.clientId,
                                        "cardNumber",
                                        event.target
                                          .value
                                      );
                                    }}
                                    className="edit-truck-controlInput"
                                    placeholder={
                                      savedCardNumberPlaceholder
                                    }
                                    maxLength={
                                      64
                                    }
                                    autoComplete="off"
                                    spellCheck="false"
                                  />
                                </FieldCard>

                                <FieldCard
                                  label={t(
                                    "labels.card_expiry",
                                    {
                                      defaultValue:
                                        "Validade",
                                    }
                                  )}
                                  icon={
                                    <FaCalendarAlt />
                                  }
                                  className="edit-truck-cardSpan-3"
                                >
                                  <MonthControl
                                    id={`edit-truck-card-expiry-${card.clientId}`}
                                    value={
                                      card.expiryMonth
                                    }
                                    onChange={(
                                      event
                                    ) => {
                                      updateCard(
                                        card.clientId,
                                        "expiryMonth",
                                        event.target
                                          .value
                                      );
                                    }}
                                    hasError={
                                      false
                                    }
                                  />
                                </FieldCard>

                                <FieldCard
                                  label={t(
                                    "labels.card_pin",
                                    {
                                      defaultValue:
                                        "PIN / código",
                                    }
                                  )}
                                  icon={
                                    <FaShieldAlt />
                                  }
                                  className="edit-truck-cardSpan-2"
                                >
                                  <div className="edit-truck-cardPinControl">
                                    <input
                                      type={
                                        pinVisible
                                          ? "text"
                                          : "password"
                                      }
                                      value={
                                        card.pin
                                      }
                                      onChange={(
                                        event
                                      ) => {
                                        updateCard(
                                          card.clientId,
                                          "pin",
                                          event.target
                                            .value
                                        );
                                      }}
                                      className="edit-truck-controlInput"
                                      placeholder={
                                        savedPinPlaceholder
                                      }
                                      maxLength={
                                        64
                                      }
                                      autoComplete="new-password"
                                      spellCheck="false"
                                    />

                                    <button
                                      type="button"
                                      className="edit-truck-cardPinToggle"
                                      onClick={() => {
                                        toggleCardPin(
                                          card.clientId
                                        );
                                      }}
                                      disabled={
                                        !card.pin
                                      }
                                      aria-label={
                                        pinVisible
                                          ? t(
                                              "cards.hide_pin",
                                              {
                                                defaultValue:
                                                  "Ocultar PIN",
                                              }
                                            )
                                          : t(
                                              "cards.show_pin",
                                              {
                                                defaultValue:
                                                  "Mostrar PIN",
                                              }
                                            )
                                      }
                                      title={
                                        card.pin
                                          ? pinVisible
                                            ? t(
                                                "cards.hide_pin",
                                                {
                                                  defaultValue:
                                                    "Ocultar PIN",
                                                }
                                              )
                                            : t(
                                                "cards.show_pin",
                                                {
                                                  defaultValue:
                                                    "Mostrar PIN",
                                                }
                                              )
                                          : t(
                                              "cards.pin_saved_help",
                                              {
                                                defaultValue:
                                                  "Introduza um novo PIN/código para substituir o atual.",
                                              }
                                            )
                                      }
                                    >
                                      {pinVisible ? (
                                        <FaEyeSlash />
                                      ) : (
                                        <FaEye />
                                      )}
                                    </button>
                                  </div>
                                </FieldCard>
                              </div>
                            </article>
                          );
                        }
                      )}
                    </div>
                  ) : (
                    <div className="edit-truck-cardsEmpty">
                      <FaCreditCard />

                      <span>
                        {t(
                          "cards.empty",
                          {
                            defaultValue:
                              "Nenhum cartão associado.",
                          }
                        )}
                      </span>
                    </div>
                  )}
                </section>

                <section className="edit-truck-sectionCard">
                  <SectionHeading
                    number="3"
                    title={t(
                      "sections.documents_validities",
                      {
                        defaultValue:
                          "Documentos & Validades",
                      }
                    )}
                    description={t(
                      "sections.documents_description",
                      {
                        defaultValue:
                          "Ficheiros legais do veículo e respetivos prazos.",
                      }
                    )}
                  />

                  <div className="edit-truck-documentsGrid">
                    {documentItems.map(
                      (
                        item
                      ) => (
                        <DocumentCard
                          key={
                            item.key
                          }
                          fieldName={
                            item.key
                          }
                          title={
                            item.title
                          }
                          file={
                            item.file
                          }
                          expiryField={
                            item.expiryField
                          }
                          expiryValue={
                            item.expiryValue
                          }
                          status={
                            item.status
                          }
                          invalidFile={
                            invalidFields[
                              item.key
                            ]
                          }
                          invalidExpiry={
                            item.expiryField
                              ? invalidFields[
                                  item.expiryField
                                ]
                              : null
                          }
                          onFileChange={
                            handleFileChange
                          }
                          onDateChange={
                            handleChange
                          }
                          onView={(
                            file,
                            title
                          ) => {
                            setViewerFile(
                              {
                                file,
                                title,
                              }
                            );
                          }}
                          t={
                            t
                          }
                        />
                      )
                    )}
                  </div>
                </section>
              </form>

              <section className="edit-truck-fuelWrap">
                <TruckFuelTab
                  truckId={
                    truckId
                  }
                  truckLabel={
                    truck.plate
                      ? `${
                          truck.plate
                        }${
                          truck.brand ||
                          truck.model
                            ? ` • ${
                                truck.brand ||
                                ""
                              } ${
                                truck.model ||
                                ""
                              }`
                            : ""
                        }`
                      : ""
                  }
                />
              </section>
            </main>
          </div>
        )}
      </div>

      {showSuccessPopup ? (
        <div className="edit-truck-successPopup-overlay">
          <div className="edit-truck-successPopup">
            <div className="edit-truck-successPopup-icon">
              ✓
            </div>

            <h3>
              {t(
                "success.updatedTitle",
                {
                  defaultValue:
                    "Camião atualizado",
                }
              )}
            </h3>

            <p>
              {t(
                "messages.updated_success",
                {
                  defaultValue:
                    "Camião atualizado com sucesso!",
                }
              )}
            </p>

            <button
              type="button"
              className="edit-truck-successPopup-btn"
              onClick={() => {
                navigate(
                  "/trucks"
                );
              }}
            >
              {t(
                "buttons.ok",
                {
                  defaultValue:
                    "OK",
                }
              )}
            </button>
          </div>
        </div>
      ) : null}

      {viewerData ? (
        <div
          className="edit-truck-viewerOverlay"
          role="dialog"
          aria-modal="true"
          onClick={() => {
            setViewerFile(
              null
            );
          }}
        >
          <div
            className="edit-truck-viewerModal"
            onClick={(
              event
            ) => {
              event.stopPropagation();
            }}
          >
            <div className="edit-truck-viewerHeader">
              <span
                className="edit-truck-viewerTitle"
                title={
                  viewerData.name
                }
              >
                {viewerData.name}
              </span>

              <div className="edit-truck-viewerActions">
                <button
                  type="button"
                  className="edit-truck-viewerActionBtn"
                  aria-label="Descarregar ficheiro"
                  title="Descarregar"
                  onClick={
                    handleDownloadViewerFile
                  }
                >
                  <FaDownload />
                </button>

                <button
                  type="button"
                  className="edit-truck-viewerClose"
                  aria-label="Fechar visualizador"
                  title="Fechar"
                  onClick={() => {
                    setViewerFile(
                      null
                    );
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="edit-truck-viewerBody">
              {viewerData.isPdf ? (
                <iframe
                  title={
                    viewerData.name ||
                    "document"
                  }
                  src={
                    viewerData.href
                  }
                  className="edit-truck-viewerFrame"
                />
              ) : viewerData.isImage ? (
                <img
                  src={
                    viewerData.href
                  }
                  alt={
                    viewerData.name ||
                    "document"
                  }
                  className="edit-truck-viewerImage"
                />
              ) : (
                <div className="edit-truck-viewerUnsupported">
                  <FaFileAlt />

                  <p>
                    {viewerData.name}
                  </p>

                  <button
                    type="button"
                    className="edit-truck-viewerDownloadFallback"
                    onClick={
                      handleDownloadViewerFile
                    }
                  >
                    <FaDownload />

                    <span>
                      Download
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default EditTruck;